import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../../App';
import { Users, Clock, Shield, AlertTriangle, Search, ChevronDown, ChevronUp, Building2, Send } from 'lucide-react';
import { toast } from 'sonner';
import ContactButtons from '../../components/ContactButtons';
import BulkEmailModal from '../../components/BulkEmailModal';

const LEVEL_LABELS = { level_1: 'Community', level_2: 'Youth', level_3: 'Vulnerable Population' };
const STATUS_COLORS = {
  verified: 'bg-green-100 text-green-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  expired: 'bg-red-100 text-red-800',
  not_started: 'bg-gray-100 text-gray-600',
};

const PartnerParticipants = () => {
  const { user, logout } = useContext(AuthContext);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [showBulkEmail, setShowBulkEmail] = useState(false);

  const fetchParticipants = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/partner/participants`);
      setParticipants(res.data);
    } catch {
      toast.error('Failed to load participants');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchParticipants(); }, [fetchParticipants]);

  const filtered = participants.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-lg">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-[#FBF4EA]" data-testid="partner-participants-page">
      {/* Partner Navigation */}
      <nav className="bg-white border-b-2 border-[#E9DDCF]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Building2 className="w-6 h-6 text-[#8B2E3C]" />
              <div className="font-bold text-lg text-[#222]">{user?.partner_org_name || 'Partner Portal'}</div>
            </div>
            <div className="flex items-center gap-4 text-sm overflow-x-auto">
              <Link to="/partner/dashboard" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Dashboard</Link>
              <Link to="/partner/opportunities" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Opportunities</Link>
              <Link to="/partner/community" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Community Board</Link>
              <Link to="/partner/participants" className="text-[#8B2E3C] font-bold whitespace-nowrap">Participants</Link>
              <Link to="/partner/invites" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Invites</Link>
              <Link to="/partner/staff" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Staff</Link>
              <Link to="/partner/time-entries" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Time Entries</Link>
              <Link to="/partner/reports" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Reports</Link>
              <button onClick={logout} className="text-[#8B2E3C] hover:underline whitespace-nowrap">Logout</button>
            </div>
          </div>
        </div>
      </nav>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#222]">Participants</h1>
            <p className="text-[#666] mt-1">Volunteers connected to your organization</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#666]">
            <Users className="w-4 h-4" />
            <span>{participants.length} participant{participants.length !== 1 ? 's' : ''}</span>
          </div>
          {user?.role === 'partner_admin' && (
            <button
              onClick={() => setShowBulkEmail(true)}
              className="btn-primary flex items-center gap-2 ml-auto"
              data-testid="partner-bulk-email-button"
            >
              <Send className="w-4 h-4" />
              Bulk Email
            </button>
          )}
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-sm text-amber-800" data-testid="privacy-disclaimer">
          <strong>Privacy Notice:</strong> Participant information is confidential and should only be used for volunteer coordination purposes. Volunteer passport information is shared only with participant consent. Do not use this information for discriminatory purposes.
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#999]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="input-field pl-10"
            data-testid="participant-search"
          />
        </div>

        {filtered.length === 0 ? (
          <div className="card text-center py-12 text-[#666]">
            {participants.length === 0 ? 'No participants have interacted with your opportunities yet.' : 'No participants match your search.'}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(p => (
              <div key={p.id} className="card" data-testid={`participant-row-${p.id}`}>
                <div
                  role="button"
                  tabIndex={0}
                  className="w-full text-left cursor-pointer"
                  onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpandedId(expandedId === p.id ? null : p.id); }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#8B2E3C] text-white flex items-center justify-center font-bold text-sm shrink-0">
                        {p.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div className="font-semibold text-[#222]">{p.name}</div>
                        <div className="text-xs text-[#666]">{p.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1 text-[#666]">
                        <Clock className="w-4 h-4" />
                        <span>{p.total_hours}h</span>
                      </div>
                      <ContactButtons
                        email={p.email}
                        phone={p.phone}
                        name={p.name}
                        compact
                      />
                      {p.shares_passport && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[p.clearance_status] || STATUS_COLORS.not_started}`}>
                          <Shield className="w-3 h-3 inline mr-1" />
                          {LEVEL_LABELS[p.clearance_level] || 'Community'}
                        </span>
                      )}
                      {p.shares_passport && p.org_alert_status === 'active' && (
                        <span className="flex items-center gap-1 text-xs text-orange-600">
                          <AlertTriangle className="w-3 h-3" /> Alert
                        </span>
                      )}
                      {expandedId === p.id ? <ChevronUp className="w-4 h-4 text-[#999]" /> : <ChevronDown className="w-4 h-4 text-[#999]" />}
                    </div>
                  </div>
                </div>

                {expandedId === p.id && (
                  <div className="mt-4 pt-4 border-t border-[#E9DDCF]">
                    <div className="grid sm:grid-cols-2 gap-6">
                      {/* Opportunities */}
                      <div>
                        <h4 className="text-sm font-bold text-[#222] mb-2">Opportunities ({p.opportunities?.length || 0})</h4>
                        {p.opportunities?.length > 0 ? (
                          <div className="space-y-1">
                            {p.opportunities.map((opp, i) => (
                              <div key={opp.id || i} className="flex items-center justify-between text-sm bg-[#FBF4EA] rounded px-3 py-2">
                                <span className="text-[#222]">{opp.title || opp.id}</span>
                                <span className={`text-xs px-2 py-0.5 rounded ${opp.status === 'approved' || opp.status === 'attended' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                  {opp.status}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-[#999]">No signups</p>
                        )}
                      </div>

                      {/* Passport */}
                      <div>
                        <h4 className="text-sm font-bold text-[#222] mb-2">Volunteer Passport</h4>
                        {p.shares_passport ? (
                          <div className="bg-[#FBF4EA] rounded-lg p-3 space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-[#666]">Clearance Level</span>
                              <span className="font-medium">{LEVEL_LABELS[p.clearance_level] || 'Community'}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#666]">Status</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[p.clearance_status] || STATUS_COLORS.not_started}`}>
                                {p.clearance_status?.replace('_', ' ')}
                              </span>
                            </div>
                            {p.clearance_expiration && (
                              <div className="flex justify-between">
                                <span className="text-[#666]">Expiration</span>
                                <span className="font-medium">{p.clearance_expiration}</span>
                              </div>
                            )}
                            {p.org_alert_status === 'active' && (
                              <div className="flex items-center gap-1 text-orange-600 text-xs mt-1">
                                <AlertTriangle className="w-3 h-3" /> Active alert on this volunteer
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="bg-gray-50 rounded-lg p-3 text-sm text-[#999] italic">
                            Passport not shared by participant
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <BulkEmailModal
        isOpen={showBulkEmail}
        onClose={() => setShowBulkEmail(false)}
        scope="partner"
        partnerOrgId={user?.partner_org_id}
      />
    </div>
  );
};

export default PartnerParticipants;
